import { ChartOptions, Plugin } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import chroma from 'chroma-js';
import history from 'history.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';

import { averageDays, buildMetricsSeries } from './chartData';
import { AGE_BANDS, BPAMetricsData, MetricStatus, MetricsView } from './types';
import {
  BAR_CHART_OPTIONS,
  DOUGHNUT_CHART_OPTIONS,
} from '../../components/BarOrDoughnutChart/options';
import { getChartLegendColor } from '../../helper';
import { BPA_DESELECTED_COLOR, BPA_SEVERITY_CONFIG } from '../Charts/config';
import { buildBPAFindingsDrillDownUrl } from '../Charts/drillDown';

interface Props {
  source: BPAMetricsData;
  status: MetricStatus;
  view: MetricsView;
  providers: string[];
  severities: number[];
  integrationIds: number[];
}

const BAND_COLORS = AGE_BANDS.map(({ label }) => getChartLegendColor(label));

/** Both metric states use the same chart and calculations, with typed v3 callbacks. */
export default function MetricsChart({
  source,
  status,
  view,
  providers,
  severities,
  integrationIds,
}: Props) {
  const [redirectUrl, setRedirectUrl] = useState<string>();
  const series = useMemo(
    () => buildMetricsSeries(source, status, providers, severities),
    [source, status, providers, severities],
  );
  const isSeverity = view === 'Severity';
  const title = status === 'open' ? 'Open Findings' : 'Closed Findings';
  const summary = series[0]?.selected;
  const labels = isSeverity
    ? BPA_SEVERITY_CONFIG.map((item) => item.label)
    : AGE_BANDS.map((item) => item.label);
  const colors = isSeverity
    ? BPA_SEVERITY_CONFIG.map((item) =>
        severities.includes(item.value) ? item.color : BPA_DESELECTED_COLOR,
      )
    : BAND_COLORS;
  const data = {
    labels,
    datasets: series.map((item) => ({
      label: item.label,
      data: isSeverity
        ? item.severities.map(averageDays)
        : AGE_BANDS.map(({ key }) => item.selected.bands[key]),
      backgroundColor: colors,
      hoverBorderColor: colors.map((color) => chroma(color).darken(2).css()),
    })),
  };

  const navigate = (datasetIndex: number, index: number) => {
    const item = series[datasetIndex];
    if (!item || index < 0 || index >= labels.length) return;
    setRedirectUrl(
      buildBPAFindingsDrillDownUrl({
        severity: severities,
        provider: providers,
        integrationIds,
        providerOverride: item.provider,
        severityOverride: isSeverity
          ? BPA_SEVERITY_CONFIG[index].value
          : undefined,
        effectiveStatus: status === 'open' ? 'Active' : 'Fixed,Ignored',
        ageBand: isSeverity ? undefined : AGE_BANDS[index].key,
      }),
    );
  };

  // Chart.js continues notifying plugins after its onClick callback. Navigate
  // from an effect so the chart remains mounted until that event is complete.
  useEffect(() => {
    if (redirectUrl) history.push(redirectUrl);
  }, [redirectUrl]);

  const barOptions: ChartOptions<'bar'> = {
    ...BAR_CHART_OPTIONS,
    interaction: { mode: 'nearest', intersect: true },
    onClick: (_event, elements) => {
      if (elements[0]) navigate(elements[0].datasetIndex, elements[0].index);
    },
    scales: {
      ...BAR_CHART_OPTIONS.scales,
      y: {
        // Precision is a linear-scale option; don't spread the shared scale union.
        type: 'linear',
        grid: BAR_CHART_OPTIONS.scales?.y?.grid,
        min: 0,
        beginAtZero: true,
        ticks: {
          padding: BAR_CHART_OPTIONS.scales?.y?.ticks?.padding,
          color: BAR_CHART_OPTIONS.scales?.y?.ticks?.color,
          precision: 1,
          callback: (value) => Number(value).toFixed(1),
        },
      },
    },
    plugins: {
      ...BAR_CHART_OPTIONS.plugins,
      datalabels: {
        ...BAR_CHART_OPTIONS.plugins?.datalabels,
        formatter: (value: number) => value.toFixed(1),
      },
      tooltip: {
        ...BAR_CHART_OPTIONS.plugins?.tooltip,
        callbacks: {
          label: (context) => {
            const item = series[context.datasetIndex];
            const metric = item.severities[context.dataIndex];
            return `${item.label}: ${averageDays(metric).toFixed(1)}`;
          },
        },
      },
    },
  };
  const doughnutOptions: ChartOptions<'doughnut'> = {
    ...DOUGHNUT_CHART_OPTIONS,
    interaction: { mode: 'nearest', intersect: true },
    onClick: (_event, elements) => {
      if (elements[0]) navigate(elements[0].datasetIndex, elements[0].index);
    },
    plugins: {
      ...DOUGHNUT_CHART_OPTIONS.plugins,
      legend: {
        ...DOUGHNUT_CHART_OPTIONS.plugins?.legend,
        // Toggle the band across every provider ring using the Chart.js v3 API.
        // Headers retain the selected cohort's totals, as in CA metrics.
        onClick: (_event, item, legend) => {
          if (item.index === undefined) return;
          legend.chart.toggleDataVisibility(item.index);
          legend.chart.update();
        },
      },
      datalabels: {
        ...DOUGHNUT_CHART_OPTIONS.plugins?.datalabels,
        // Percentages follow the visible arcs after a legend click; raw cycle
        // counts stay in the tooltip and the underlying metrics are unchanged.
        formatter: (value: number, context) => {
          let total = 0;
          context.dataset.data.forEach((count, index) => {
            if (
              typeof count === 'number' &&
              context.chart.getDataVisibility(index)
            )
              total += count;
          });
          const percentage =
            total > 0 ? ((value * 100) / total).toFixed(0) : '0';
          return percentage === '0' ? '' : `${percentage}%`;
        },
      },
      tooltip: {
        ...DOUGHNUT_CHART_OPTIONS.plugins?.tooltip,
        callbacks: {
          title: (items) => items[0]?.label || '',
          label: (context) =>
            `${series[context.datasetIndex].label}: ${context.raw}`,
        },
      },
    },
  };

  return (
    <Card className="card-chart border border-light">
      <CardHeader>
        <Row>
          <Col className="d-flex flex-row align-items-baseline">
            <h1 className="d-inline mb-0">
              {summary ? averageDays(summary).toFixed(1) : '0.0'}
            </h1>
            <h3 className="card-category px-1">
              {status === 'open'
                ? 'days (Average Time Open)'
                : 'days (Average Time to Closure)'}
            </h3>
          </Col>
          <Col>
            <div className="d-flex flex-row d-inline float-right align-items-baseline">
              <h1 className="d-inline mb-0">{summary?.count ?? 0}</h1>
              <h3 className="card-category px-1">{title}</h3>
            </div>
          </Col>
        </Row>
      </CardHeader>
      <CardBody style={{ cursor: summary?.count ? 'pointer' : 'default' }}>
        {!summary?.count ? (
          <p role="status">No eligible cycles for the selected filters.</p>
        ) : (
          <div className="chart-area">
            {isSeverity ? (
              <Bar
                data={data}
                options={barOptions}
                plugins={[ChartDataLabels]}
                role="img"
                aria-label={`${title}: average age by severity`}
              />
            ) : (
              <Doughnut
                data={data}
                options={doughnutOptions}
                plugins={[ChartDataLabels as Plugin<'doughnut'>]}
                role="img"
                aria-label={`${title}: cycle counts by age band`}
              />
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
